import React, { useState } from 'react';
import axios from 'axios';
import { PhoneCall, MessageSquareReply, Sunrise, BarChart3, ArrowRight, Loader2, MailCheck, Download } from 'lucide-react';
import { ChatWidget } from '../components/custom/ChatWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { icon: PhoneCall, title: 'AI call grading', body: 'Every call transcribed and scored against your own playbook within minutes of hang-up — no sampling, no listening back.' },
  { icon: Sunrise, title: 'Revivable deals', body: 'Prospects that slipped away are ranked overnight and handed to your team each morning with the reason to call.' },
  { icon: MessageSquareReply, title: 'SMS reply inbox', body: 'Every text in and out with a 15-minute reply countdown, one-tap templates and manager visibility.' },
  { icon: BarChart3, title: 'Manager scorecards', body: 'Per-rep performance, response-time slippage and coaching records — your Monday meeting, pre-built.' },
];

const DEMOS = [
  { path: '/demo', label: 'Gyms & wellness', brand: 'Gym & Wellness 360' },
  { path: '/demo/carsales', label: 'Car dealerships', brand: 'Velocity Motors Group' },
  { path: '/demo/helpdesk', label: 'IT service desks', brand: 'Apex IT Services' },
];

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', industry: 'gym', message: '' });
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setState('sending');
    try {
      await axios.post(`${API}/contact`, form);
      setState('sent');
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Something went wrong — please try again.');
      setState('error');
    }
  };
  if (state === 'sent') {
    return (
      <div className="text-center py-10" data-testid="contact-success">
        <MailCheck className="w-10 h-10 text-teal-600 mx-auto mb-3" />
        <p className="font-bold text-slate-900 text-lg">Thanks — we'll be in touch within one business day.</p>
        <p className="text-sm text-slate-500 mt-2">Your free guide is on its way to your inbox.</p>
        <a href={`${API}/public/guide.pdf`} className="inline-flex items-center gap-2 mt-4 text-teal-700 font-semibold text-sm hover:underline" data-testid="contact-guide-link">
          <Download className="w-4 h-4" /> Or download the guide now
        </a>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="contact-form">
      {[['name', 'Full name', 'text'], ['email', 'Work email', 'email'], ['phone', 'Phone number', 'tel'], ['company', 'Company', 'text']].map(([k, label, type]) => (
        <label key={k} className="block">
          <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide">{label}</span>
          <input type={type} required={k !== 'company'} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className="mt-1 w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid={`contact-${k}`} />
        </label>
      ))}
      <label className="block">
        <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide">Your industry</span>
        <select value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
          className="mt-1 w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          data-testid="contact-industry">
          <option value="gym">Gym / wellness</option>
          <option value="carsales">Car dealership</option>
          <option value="helpdesk">IT / service desk</option>
          <option value="other">Something else</option>
        </select>
      </label>
      <label className="block sm:col-span-2">
        <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide">Message</span>
        <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="mt-1 w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          data-testid="contact-message" />
      </label>
      {state === 'error' && <p className="text-sm text-red-600 sm:col-span-2" data-testid="contact-error">{msg}</p>}
      <button type="submit" disabled={state === 'sending'}
        className="sm:col-span-2 bg-teal-600 text-white font-bold py-3 rounded-full hover:bg-teal-500 inline-flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="contact-submit">
        {state === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Send + get the free guide (PDF)
      </button>
    </form>
  );
};

export default function HomePage() {
  return (
    <div className="bg-slate-50 text-slate-900 min-h-screen" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="home-page">
      <header className="flex items-center justify-between px-6 sm:px-16 py-5 border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-40">
        <p className="font-bold text-lg tracking-tight">Yabbit <span className="text-teal-600">VoiceFabric</span></p>
        <div className="flex items-center gap-3">
          <a href="/demo" className="text-sm font-semibold text-slate-600 hover:text-teal-700" data-testid="home-demo-link">Live demo</a>
          <a href="/login" className="text-sm font-bold bg-slate-900 text-white px-4 py-2 rounded-full hover:bg-slate-700" data-testid="home-signin-link">Sign in</a>
        </div>
      </header>

      <section className="px-6 sm:px-16 pt-16 pb-14 max-w-5xl">
        <p className="text-xs font-bold text-teal-700 uppercase tracking-[0.25em] mb-4">Call intelligence for sales & service teams</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl">Every call graded.<br />Every deal <span className="text-teal-600">rescued.</span></h1>
        <p className="text-slate-600 mt-5 max-w-xl text-base sm:text-lg">VoiceFabric listens to every phone call your team takes, grades it against your playbook, and hands back the deals that were about to walk away.</p>
        <div className="flex flex-wrap items-center gap-3 mt-8">
          <a href="#contact" className="bg-teal-600 text-white font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:bg-teal-500" data-testid="home-cta-contact">Talk to us <ArrowRight className="w-4 h-4" /></a>
          <a href="/demo" className="border border-slate-300 font-semibold px-6 py-3 rounded-full hover:border-teal-500 hover:text-teal-700" data-testid="home-cta-demo">Open the live demo</a>
        </div>
      </section>

      <section className="px-6 sm:px-16 pb-14">
        <img src="/demo-shots/dashboard.jpg" alt="VoiceFabric dashboard" className="w-full max-w-5xl rounded-2xl border border-slate-200 shadow-xl" data-testid="home-hero-shot" />
      </section>

      <section className="px-6 sm:px-16 pb-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl">
        {FEATURES.map((f) => (
          <div key={f.title} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-teal-400 transition-colors" data-testid={`home-feature-${f.title.split(' ')[0].toLowerCase()}`}>
            <f.icon className="w-6 h-6 text-teal-600 mb-3" />
            <p className="font-bold">{f.title}</p>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>

      <section className="px-6 sm:px-16 pb-16 max-w-6xl">
        <h2 className="text-lg font-bold text-teal-700 uppercase tracking-[0.2em] mb-2">Built for your industry</h2>
        <p className="text-2xl font-bold mb-6">Try a live demo loaded with realistic data.</p>
        <div className="grid sm:grid-cols-3 gap-5">
          {DEMOS.map((d) => (
            <a key={d.path} href={d.path} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-teal-500 hover:shadow-lg transition-all group" data-testid={`home-vertical-${d.path.split('/').pop() || 'gym'}`}>
              <p className="font-bold text-lg">{d.label}</p>
              <p className="text-sm text-slate-500 mt-1">Demo: {d.brand}</p>
              <p className="text-teal-700 font-semibold text-sm mt-4 inline-flex items-center gap-1">Open microsite <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" /></p>
            </a>
          ))}
        </div>
        <p className="text-sm text-slate-500 mt-4">Typical result: <span className="font-bold text-slate-800">8–20% of revivable deals recovered</span> — run the numbers in each demo's ROI calculator.</p>
      </section>

      <section id="contact" className="px-6 sm:px-16 py-16 bg-white border-t border-slate-200">
        <div className="max-w-3xl">
          <h2 className="text-lg font-bold text-teal-700 uppercase tracking-[0.2em] mb-2">Contact</h2>
          <p className="text-2xl font-bold mb-2">Tell us about your team.</p>
          <p className="text-sm text-slate-500 mb-7">We'll reply within one business day — and you'll get our free guide, <em>Every call graded, every deal rescued</em>, straight away.</p>
          <ContactForm />
        </div>
      </section>

      <footer className="px-6 sm:px-16 py-8 text-xs text-slate-400 border-t border-slate-200">
        Yabbit VoiceFabric · CallSense — voicefabric.com.au · <a href="/login" className="hover:text-teal-700">Customer sign in</a>
      </footer>
      <ChatWidget />
    </div>
  );
}
